// Direct imports
import { TransformNode } from "@babylonjs/core/Meshes/transformNode";
import { AbstractMesh } from "@babylonjs/core/Meshes/abstractMesh";
import { Matrix, Vector3 } from "@babylonjs/core/Maths/math.vector";
import { SceneObject } from "../core/SceneObject";
import { writesInstancePerPlacement } from "../core/instanceWrites";
import { TraceBehavior } from "./tracebehavior";
import { Logger } from '../utils/Logger';
import { ModelData } from '../scenedata/behaviors/modeldata';
import { ValidationError } from '../scenedata/behaviordata';
import { loadModel } from '../utils/ModelLoader';
import { convertKHRInteractivityToGraphData, convertKHRInteractivityToAllGraphs } from './behaviorgraph/KHRInteractivityConverter';
import { recollapseSubgraphs } from './behaviorgraph/subgraphRecollapse';
import { BehaviorGraph } from './behaviorgraph/behaviorgraph';
import { isLayoutSizeReceiver, invalidateNearestLayout } from './layout';
import type { LayoutSizeProvider, LayoutFit } from './layout';
import { bakeKindOf } from '../loaders/restoreBakedSrc';
import { getSavedPrimitiveType } from '../export/BuiltInPrimitives';

/**
 * Behavior for loading 3D models (GLB, GLTF, SPZ, PLY, SPLAT).
 *
 * Delegates mesh loading to loadModel() which promotes the GLB content
 * directly under the SceneObject transform (disposing Babylon's __root__).
 *
 * Scene-authoritative formats (.trace) set `sceneObject._resetAssetTransform`
 * to discard the GLB root transform — the scene transform is the sole source
 * of truth. Older .zip scenes preserve GLB root transforms.
 */
export class Model extends TraceBehavior<ModelData> implements LayoutSizeProvider {
    static readonly behaviorType = "Model" as const;

    /**
     * Authored fit policy, defaulting to `none` — the node's transform
     * scale is the user's sizing handle for a model, and the layout
     * measurement below already includes it, so layout must neither write
     * it nor restore identity the way it does for glyph-sized providers.
     *
     * `cover` is accepted at the wire and clamped here rather than in the
     * engine's fit arithmetic, because whether cover is honest is a
     * property of the CONTENT, not of the fit: cover fills the box by
     * overflowing it, which is only truthful when the content's own
     * geometry is the clip (a media quad's crop). A mesh has no such clip,
     * so a covered model would poke through its neighbours.
     *
     * STATEFUL — do not memoize or hoist. The clamp warns once per behavior
     * and flips `_warnedCoverClamp` to do it, so reading this getter is a
     * write. Layout reads it once per solve, which is what makes "once"
     * mean once; caching the value would silence the warning for a fit
     * committed later, and calling it speculatively would spend it early.
     */
    get layoutFit(): LayoutFit {
        const authored = this._resolved.fit ?? 'none';
        if (authored !== 'cover') return authored;
        if (!this._warnedCoverClamp) {
            this._warnedCoverClamp = true;
            Logger.warn(
                `[Model] "${this.sceneObject.name}": fit "cover" needs clipping the engine `
                + `cannot do for a mesh — using "contain" so the model stays inside its slot.`,
            );
        }
        return 'contain';
    }

    /** One warning per behavior, not one per layout pass. */
    private _warnedCoverClamp = false;

    /** Vertical seat of a fitted model inside its slot — see `ModelData.fitAnchor`. */
    get layoutFitAnchor(): 'center' | 'bottom' {
        return this._resolved.fitAnchor === 'bottom' ? 'bottom' : 'center';
    }

    /**
     * Validate and migrate model data. Mutates `data` in place for the
     * migration, the same contract Frame's validate() documents.
     *
     * An out-of-range `fit` is DELETED as well as reported, because this
     * runs on two paths that want opposite things and the delete is what
     * makes one of them safe:
     *
     *  - the CONSTRUCTOR (below) hands it `_authored` straight off the wire
     *    and ignores the return. A .trace carrying `fit: "squish"` used to
     *    load silently and then brick the object: `_applyCommit` validates
     *    the full MERGED `_authored`, so the next commit of ANY Model field
     *    failed INVALID_FIT and rolled back. Dropping the poison lets the
     *    default apply and keeps the behavior editable.
     *  - a COMMIT still gets the error, and still rejects: `_applyCommit`
     *    restores its `prevAuthored` snapshot whenever validate returns
     *    errors, so the delete this made to the merged data is thrown away
     *    with the rest of the bad patch. A user committing `fit: "squish"`
     *    sees INVALID_FIT and keeps the fit they had.
     *
     * That is why there is no "migrating" flag: the rollback in
     * `_applyCommit` already separates the two, and one code path stays
     * one code path.
     */
    protected validate(data: ModelData): ValidationError[] {
        // TS can't guard wire data — a .trace file can carry anything here.
        const fit = data.fit as unknown;
        if (fit !== undefined && fit !== 'fill' && fit !== 'contain' && fit !== 'scale-down'
            && fit !== 'cover' && fit !== 'none') {
            delete data.fit;
            return [{
                field: 'fit',
                message: `fit must be 'fill', 'contain', 'scale-down', 'cover' or 'none'`,
                code: 'INVALID_FIT',
            }];
        }
        const anchor = data.fitAnchor as unknown;
        if (anchor !== undefined && anchor !== 'center' && anchor !== 'bottom') {
            // Same two-path contract as `fit` above: drop the poison so a
            // loaded scene stays editable, report it so a commit rejects.
            delete data.fitAnchor;
            return [{
                field: 'fitAnchor',
                message: `fitAnchor must be 'center' or 'bottom'`,
                code: 'INVALID_FIT_ANCHOR',
            }];
        }
        return [];
    }

    /** The asset path used with the container cache, for releasing on detach */
    private _cachedSrcPath: string | undefined;

    /** Extensions used by the loaded model (e.g., KHR_materials_unlit) */
    public extensionsUsed: string[] = [];

    /**
     * Babylon's KHR_interactivity diagnostics from the last load (see
     * `ModelLoadResult.khrDiagnostics`) — empty when the GLB has no graph or
     * every graph is conformant. Carried for the editor to surface.
     */
    public khrDiagnostics: string[] = [];

    /** The top-level node parented under the SceneObject transform (promoted GLB child) */
    private _modelRoot: TransformNode | null = null;

    /** When true, reset the GLB root transform to identity on load */
    private _resetAssetTransform: boolean;

    constructor(sceneObject: SceneObject, data: ModelData) {
        super(sceneObject, data);
        // Nothing else validates the LOAD path — SceneCreation builds
        // behaviors with `new Ctor(...)` and TraceBehavior's constructor
        // doesn't validate — so migrate here, Frame's precedent. Re-resolve
        // after: the base constructor derived `_resolved` from the data as
        // it arrived, before the migration removed anything from it.
        this.validate(this.getAuthored());
        this._resolved = this._resolveAll();

        this._resetAssetTransform = sceneObject._resetAssetTransform;
    }

    async load(): Promise<void> {
        const src = this.sceneObject.src;
        const name = this.sceneObject.name;
        Logger.debug(`[Model] Loading model ${name}`);

        if (!src) {
            Logger.debug(`[Model] No src specified for ${name}`);
            return;
        }

        // A scene reference is not a model. `src` is a `.trace`/`.tracex`
        // archive or a library reference — `https://<api>/assets/<guid>/file`,
        // which 302s to a glTF *document* (CONTENT_MODEL_SPEC §3.4) — and the
        // nested-scene path in TraceScene owns building its content. Handing
        // either to the model loader can only fail, after a wasted download:
        // for the extension-less library URL it failed loudly, as Babylon's
        // `No plugin or fallback for …`, on a load that had in fact succeeded.
        //
        // Reachable from scenes SAVED by earlier builds, where the implied
        // Model synthesised on the reference stand-in was serialised into the
        // document's node extras. Nothing authors it any more (see
        // `SceneCreation.applyComponents`), so this is the read side of that
        // fix, not a second policy.
        // …unless the reference RESOLVED to a model file and this placement is
        // what owns it (SCENE_ROOT_SPEC §2.3, row 1). The nested load decided
        // that, fetched the bytes and recorded them on `_standInFileSrc`
        // (`TraceScene._fillReferenceWithFile`), so there is a real file behind
        // this `src` and the mapping that reaches it — which is exactly what a
        // reference resolving to a DOCUMENT does not have.
        if (this.sceneObject.isSceneReference && !this.sceneObject._standInFileSrc) {
            Logger.debug(`[Model] ${name}: "${src}" is a scene reference — the nested-scene load owns it`);
            return;
        }

        try {
            // Parent to transform during load so the mesh is always in the
            // scene graph. After the await, reparent to the animation root if
            // one was created by Animation.load() during our load.
            // Behaviors that mutate geometry or assign per-mesh materials need
            // real clones: InstancedMesh shares its source's geometry, morph
            // target manager, and material, so MorphStretch influences and
            // Renderer slot materials would alias across every instance of the
            // cached container (the same trap Frame documents for its parts).
            // Read from authored data — sibling behaviors load in parallel and
            // are not registered yet when this runs.
            // …and anything registered as writing this placement's instance
            // (a `TRACE_animation_binding` target drives its morph weights:
            // two recordings of one avatar must not share a manager) — see
            // `core/instanceWrites.ts`.
            const needsUniqueMeshes = (this.sceneObject._getBehaviorData() ?? [])
                .some(b => b['@type'] === 'MorphStretch' || b['@type'] === 'MeshBackground' || b['@type'] === 'Bevel' || b['@type'] === 'Renderer')
                || writesInstancePerPlacement(this.sceneObject);
            const result = await loadModel(src, this.scene, this.resources, {
                parent: this.sceneObject.transform,
                resetTransform: this._resetAssetTransform,
                cloneMeshes: needsUniqueMeshes,
            });
            // Hidden until attach. Sibling loads can still be running, and the
            // behaviors that size and paint this instance (MorphStretch,
            // Renderer) only touch it in onAttach, so a frame drawn before
            // then shows the file at its own size in its own materials.
            result.root.setEnabled(false);
            // If Animation created the vroot during our await, move the mesh
            // under it. If Animation hasn't loaded yet, getOrCreateAnimationRoot
            // will reparent all transform children when it runs.
            const vroot = this.sceneObject.animationRoot;
            if (vroot && result.root.parent !== vroot) {
                result.root.parent = vroot;
            }

            this._modelRoot = result.root;
            this._cachedSrcPath = result.containerPath || undefined;

            // extensionsUsed is populated by ModelLoader for direct-load GLBs
            // (parsed from the GLB JSON chunk header before loading).
            if (result.extensionsUsed) {
                this.extensionsUsed = result.extensionsUsed;
                this.sceneObject.extensionsUsed = result.extensionsUsed;
            }

            this.khrDiagnostics = result.khrDiagnostics ?? [];

            if (result.instantiatedEntries) {
                this.sceneObject.setAnimationClips(result.instantiatedEntries.animationGroups, `${name}_`);
            }

            // If the GLB contains KHR_interactivity, convert and attach as a
            // BehaviorGraph so the graph data is visible to the editor.
            // Skip if the object already has a user-authored BehaviorGraph.
            //
            // Mark as khrManaged so the BehaviorGraph delegates runtime to
            // Babylon's coordinator (which already compiled the graph with our
            // registered trace blocks) instead of creating a duplicate.
            if (result.khrInteractivity && !this.sceneObject.getBehavior(BehaviorGraph)) {
                const hasSubgraphs = result.khrInteractivity.graphs.length > 1;
                if (hasSubgraphs) {
                    const { graphs, activeGraph } = convertKHRInteractivityToAllGraphs(result.khrInteractivity);
                    const mainGraph = graphs[activeGraph];
                    if (mainGraph && mainGraph.nodes.length > 0) {
                        await this.sceneObject.setBehavior(BehaviorGraph, { graphs, activeGraph });
                    }
                } else {
                    const graphData = convertKHRInteractivityToGraphData(result.khrInteractivity);
                    if (graphData.nodes.length > 0) {
                        // Flattened-for-export graphs carry re-collapse tags in
                        // `extras`; recover the author's subgraphs when they are
                        // there, otherwise keep the flat graph as-is.
                        const recollapsed = recollapseSubgraphs(graphData);
                        await this.sceneObject.setBehavior(
                            BehaviorGraph,
                            recollapsed
                                ? { graphs: recollapsed.graphs, activeGraph: recollapsed.activeGraph }
                                : { graph: graphData },
                        );
                    }
                }
                // Mark as KHR-managed — Babylon's loader already compiled the graph
                const bg = this.sceneObject.getBehavior(BehaviorGraph);
                if (bg) {
                    bg.khrManaged = true;
                    Logger.debug(`[Model] Attached KHR-managed BehaviorGraph from KHR_interactivity`);
                }
            }

            Logger.debug(`[Model] Successfully loaded ${name}`);
        } catch (error) {
            Logger.warn(`[Model] Failed to load ${name}: ${String(error)}`);
            throw error;
        }
    }

    protected onDetach(): void {
        // Dispose the model hierarchy including child meshes and transform nodes
        // (first arg = false means "don't keep children", so children ARE disposed).
        // Do NOT dispose materials (second arg = false) — they are shared
        // with the container's source meshes. The container owns them and
        // will dispose them when its refcount reaches 0.
        if (this._modelRoot) {
            Logger.debug(`[Model] Disposing model root for ${this.sceneObject.name}`);
            this._modelRoot.dispose(false, false);
            this._modelRoot = null;
        }

        // Release container reference
        if (this._cachedSrcPath) {
            this.resources.releaseContainer(this._cachedSrcPath);
            this._cachedSrcPath = undefined;
        }

        // A removed model leaves its container hugging a ghost size —
        // re-solve so the arrangement reflects the geometry that is gone.
        invalidateNearestLayout(this.sceneObject);
    }

    /**
     * Bounding dimensions of the loaded model in the node's parent frame —
     * the model geometry under this node's own TRS (scale included), with
     * every ancestor transform above the parent factored out.
     *
     * Returns null if no model is loaded yet (or no measurable geometry).
     */
    getDimensions(): { x: number; y: number; z: number } | null {
        return this._measureLayoutBounds('parent')?.size ?? null;
    }

    // ── LayoutSizeProvider ──

    /**
     * Not for an export Bevel bake. That file is a `builtin:` cube or plane
     * with its corners rounded, written at unit size under the node's scale
     * (`TraceExport.bakeBevelNodes`), so the scale is its size, as it is for
     * the primitive. A plain primitive leaf has layout write its slot box into
     * the scale; as a provider this Model would instead keep the saved scale
     * (`fit: none`) and a LayoutItem resize would never reach the card body.
     * `fill` is not the primitive's policy either: it leaves a zero-depth
     * slot at the bake's 1 m depth where the primitive gets MIN_VISIBLE_SCALE.
     *
     * Nor for a saved built-in primitive (`./assets/primitive_cube.glb`): the
     * same unit mesh under a scale that is its size, so a width edit must be
     * able to write the scale, as it does before the save.
     *
     * Nor for an extruded Text's body bake: the Text beside it measures the
     * node, and the body is drawn in the Text's own frame.
     */
    get providesLayoutSize(): boolean {
        const src = this.sceneObject.src;
        if (typeof src === 'string' && getSavedPrimitiveType(src)) return false;
        const bake = bakeKindOf(src);
        return bake !== 'bevel' && bake !== 'text';
    }

    /**
     * The bounds and centre this reports are in the node's PARENT's frame, the
     * node's own turn and scale in them (`_measureLayoutBounds`): a node that
     * hugs what it holds takes them as they are, and does not turn them again.
     */
    readonly layoutContentFrame = 'parent' as const;

    /**
     * Measured bounds of the loaded model geometry in the node's parent
     * frame. Null until the mesh arrives — layout falls back to the
     * authored size, and load() invalidates the nearest Layout so Hug
     * re-solves with real geometry. Read per solve, never per frame.
     *
     * DECLINES (null) when a LayoutSizeReceiver co-exists on this node: a
     * receiver realizes the solver's box in geometry (MorphStretch drives
     * morphs), so live hierarchy bounds ARE the previous solve's output — a
     * receiver-driven component with no authored sizes would otherwise feed
     * the solver its own result through geometry, pass after pass. The
     * check is the exact duck-type `_applyResults` feeds receivers with.
     */
    getLayoutContentSize(): { x: number; y: number; z: number } | null {
        for (const behavior of this.sceneObject.getBehaviors()) {
            if (isLayoutSizeReceiver(behavior)) return null;
        }
        return this._measureLayoutBounds('parent')?.size ?? null;
    }

    /**
     * The geometry's size with the node's own transform factored OUT — the
     * basis a fit divides the slot by.
     *
     * Deliberately not `getLayoutContentSize() / nodeScale`: under a fit the
     * node scale is layout's own previous write, so that division feeds each
     * pass's rounding into the next one's input. Measuring in the node's
     * frame reads the asset instead, and reads the same number every pass
     * however many times the fit has re-scaled the node.
     */
    getLayoutIntrinsicSize(): { x: number; y: number; z: number } | null {
        return this._measureLayoutBounds('node')?.size ?? null;
    }

    /**
     * Visual centre of the loaded geometry relative to the node origin.
     * GLB pivots commonly sit at the base (or anywhere the artist left
     * them), so layout places the bounds centre — not the pivot — at the
     * slot centre.
     */
    getLayoutContentCenter(): { x: number; y: number; z: number } | null {
        return this._measureLayoutBounds('parent')?.center ?? null;
    }

    /**
     * AABB of the loaded model in the node's PARENT-local frame — the frame
     * the solver computes slot boxes in.
     *
     * Scope: the `_modelRoot` subtree ONLY. Child SceneObjects (a Text
     * label) and sibling behaviors' meshes are not the model's intrinsic
     * size — and a layout-managed descendant is scale-written by the solver,
     * so measuring it would feed the solver its own output back.
     *
     * Basis: per-mesh local bounding corners composed with
     * (parentWorld⁻¹ × meshWorld). Every transform ABOVE the node's parent
     * cancels exactly (an AR image-anchor yawing the scene root at view time
     * must not inflate spans), while the node's own TRS stays in — the
     * measurement is "geometry × node scale", the documented contract for a
     * scale-preserving provider.
     *
     * The centre is reported relative to the node origin in that same
     * parent-local basis (the node's origin in its parent's frame is its
     * local position), so `_applyResults` can subtract it from the
     * parent-local slot position directly — never a world-basis delta.
     */
    private _measureLayoutBounds(basis: 'parent' | 'node'): {
        size: { x: number; y: number; z: number };
        center: { x: number; y: number; z: number };
    } | null {
        if (!this._modelRoot) return null;

        const frame = basis === 'node'
            ? this.sceneObject.transform
            : this.sceneObject.transform.parent;
        const toParent = frame
            ? Matrix.Invert(frame.computeWorldMatrix(true))
            : Matrix.Identity();

        const meshes: AbstractMesh[] = this._modelRoot.getChildMeshes(false);
        if (this._modelRoot instanceof AbstractMesh) meshes.push(this._modelRoot);

        const min = new Vector3(Infinity, Infinity, Infinity);
        const max = new Vector3(-Infinity, -Infinity, -Infinity);
        const corner = new Vector3();
        for (const mesh of meshes) {
            if (mesh.getTotalVertices() === 0) continue;
            mesh.computeWorldMatrix(true);
            for (const worldCorner of mesh.getBoundingInfo().boundingBox.vectorsWorld) {
                Vector3.TransformCoordinatesToRef(worldCorner, toParent, corner);
                min.minimizeInPlace(corner);
                max.maximizeInPlace(corner);
            }
        }

        const size = { x: max.x - min.x, y: max.y - min.y, z: max.z - min.z };
        // No measurable geometry yet (all meshes empty / still in flight):
        // spans stay at their sentinel extremes — decline to provide.
        if (!isFinite(size.x) || !isFinite(size.y) || !isFinite(size.z) ||
            size.x < 0 || size.y < 0 || size.z < 0) {
            return null;
        }

        // The centre is reported relative to the node origin in the parent
        // frame; in the node's own frame the origin IS the frame's, so the
        // offset is zero and only `size` is meaningful.
        const origin = basis === 'node'
            ? Vector3.Zero()
            : this.sceneObject.transform.position;
        return {
            size,
            center: {
                x: (min.x + max.x) / 2 - origin.x,
                y: (min.y + max.y) / 2 - origin.y,
                z: (min.z + max.z) / 2 - origin.z,
            },
        };
    }

    /**
     * A changed fit is a changed size: re-solve so the new policy is
     * realized without waiting for some other edit to dirty the layout.
     */
    protected async applyCommit(patch: Partial<ModelData>, _prev: ModelData): Promise<void> {
        if ('fit' in patch) invalidateNearestLayout(this.sceneObject);
    }

    protected onAttach(_target: TransformNode): void {
        Logger.debug(`[Model] Attaching to ${_target.name}`);
        if (!this._modelRoot) return;
        this._modelRoot.setEnabled(true);
        // Bounds-dependent layout (Hug sizing, slot centering, a measured
        // ancestor's subtree) solved against nothing while the mesh was in
        // flight or hidden — re-solve with the geometry now drawn.
        invalidateNearestLayout(this.sceneObject);
    }
}
